import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import { useLivePanchang } from "@/lib/usePanchang";
import { getSavedLocation, nowInLocation, hmOf } from "@/lib/cities";
import { fieldDefault, ovCell } from "@/lib/panchangTamil";
import LocationPicker from "@/components/LocationPicker";
import { CalendarDays, ArrowRight, Clock, Info } from "lucide-react";
import { SunriseIcon, SunsetIcon, SunIcon } from "@/components/CelestialIcons";

// Window time "4:46 AM–5:37 AM" → two no-wrap halves so narrow cards break between
// start and end, never inside a time.
const spanParts = (v) => {
  if (!/\d/.test(String(v || ""))) return "—"; // no time today (e.g. Abhijit on Wednesday)
  const mm = String(v).match(/^(.+?)\s*[–-]\s*(.+)$/);
  return mm ? <><span className="whitespace-nowrap">{mm[1]}</span>{" "}<span className="whitespace-nowrap">– {mm[2]}</span></> : (v || "—");
};


const DEFAULT_LOC = { name: "New Delhi", tz: 330, tz_id: "Asia/Kolkata" };
const todayLabel = (lang) =>
  new Date().toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

// Is the clock time `hm` inside the span "HH:MM–HH:MM" (any dash)?
function timeInSpan(span, hm) {
  if (!span || !hm) return false;
  const parts = String(span).split(/[–—-]/).map((s) => s.trim());
  if (parts.length !== 2) return false;
  const toMin = (t) => {
    const m = String(t).trim().match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
    if (!m) return NaN;
    let h = +m[1]; const min = +m[2]; const ap = (m[3] || "").toUpperCase();
    if (ap === "PM" && h !== 12) h += 12;
    if (ap === "AM" && h === 12) h = 0;
    return h * 60 + min;
  };
  const [s, e] = parts.map(toMin);
  const t = toMin(hm);
  if ([s, e, t].some(Number.isNaN)) return false;
  return s <= e ? t >= s && t < e : t >= s || t < e;
}

// Single, merged homepage Panchangam panel. Its data is fetched STRICTLY from the
// same live Swiss Ephemeris payload as the full /panchang page (via useLivePanchang
// → mapPanchang), so every card matches the full page exactly. All timings are in
// the selected location's timezone, with a live "Now" highlight on the active window.
export default function HomePanchang() {
  const { pick, lang } = useLang();
  const { panchang_tamil = {}, panchang_lang_fonts = {} } = useSettings();
  const [locKey, setLocKey] = useState(0);
  const { data, error } = useLivePanchang(undefined, locKey);
  const [loc, setLoc] = useState(() => getSavedLocation() || DEFAULT_LOC);
  const [now, setNow] = useState(() => hmOf(nowInLocation(getSavedLocation() || DEFAULT_LOC)));

  // Compute note text in the site language (honours admin override → default → English).
  const noteLang = ["hi", "ta", "te"].includes(lang) ? lang : "en";
  const noteFont = noteLang === "ta" ? "font-tamil-body" : noteLang === "te" ? "font-telugu-body" : noteLang === "hi" ? "font-deva-body" : "";
  const uiCell = (key) => ovCell(panchang_tamil, "ui", key);
  const uiText = (key) => (noteLang === "en" ? (uiCell(key).en || fieldDefault("ui", key, "en")) : (uiCell(key)[noteLang] || fieldDefault("ui", key, noteLang) || fieldDefault("ui", key, "en")));
  const lf = panchang_lang_fonts || {};
  const scopeFontCss = [
    lf.ta && `[data-pf-lang-scope] .font-tamil-body{font-family:'${lf.ta}','Noto Serif Tamil',serif !important;}`,
    lf.te && `[data-pf-lang-scope] .font-telugu-body{font-family:'${lf.te}','Noto Serif Telugu',serif !important;}`,
    lf.hi && `[data-pf-lang-scope] .font-deva-body{font-family:'${lf.hi}','Noto Serif Devanagari',serif !important;}`,
  ].filter(Boolean).join("");

  useEffect(() => {
    setLoc(getSavedLocation() || DEFAULT_LOC);
  }, [locKey]);

  // Refresh the "now" clock (in the location's timezone) every 30s so the
  // active-window badge stays live.
  useEffect(() => {
    const tick = () => setNow(hmOf(nowInLocation(loc)));
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [loc]);

  const place = loc?.name || (data && data.place) || "New Delhi";

  // Header shared by the loaded, loading and unavailable states so the section
  // (and its location picker) never disappears.
  const Header = (
    <div className="flex flex-col gap-5 border-b border-line pb-6 lg:flex-row lg:items-end lg:justify-between">
      <div>
        <p className="overline-label mb-2 text-gold">{pick("आज का पंचांग", "Today's Panchangam")}</p>
        <h2 className="font-deva text-3xl text-ink deva-lh md:text-4xl">पंचांग</h2>
        <p className="mt-2 flex items-center gap-2 text-sm text-ink-2">
          <CalendarDays className="h-3.5 w-3.5 shrink-0 text-saffron" />
          {todayLabel(lang)}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="flex flex-col gap-1">
          <span className="overline-label text-ink-3">{pick("स्थान", "Location")}</span>
          <LocationPicker onChange={() => setLocKey((k) => k + 1)} />
        </div>
        <Link to="/panchang" data-testid="home-panchang-full" className="link-underline inline-flex items-center gap-1.5 pb-1 text-sm text-saffron">
          {pick("पूर्ण पंचांग", "Full Panchangam")} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );

  // Payload unavailable for this place (e.g. the ephemeris service cannot compute
  // extreme-latitude / polar day-night). Show the SAME message the full page would
  // — strictly reflecting the payload, never a second engine's guess.
  if (!data) {
    return (
      <section className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-20" data-testid="home-panchang">
        <div className="rounded-2xl border border-line bg-[var(--surface)] p-5 shadow-[0_1px_2px_rgba(36,28,21,0.04),0_24px_60px_-40px_rgba(36,28,21,0.35)] md:p-8">
          {Header}
          <div className="flex items-start gap-3 py-8 text-ink-2" data-testid="home-panchang-unavailable">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
            <p className="text-sm leading-relaxed">
              {pick(
                "इस स्थान का पंचांग अभी उपलब्ध नहीं है — कृपया कोई अन्य स्थान चुनें।",
                "Panchangam for this location is unavailable right now — please choose another location."
              )}
              {error && <span className="mt-1 block text-xs text-ink-3">{error}</span>}
            </p>
          </div>
        </div>
      </section>
    );
  }

  const m = data.muhurta || {};
  const till = (v) => (v ? <span className="ml-1.5 whitespace-nowrap text-sm font-normal text-ink-3">{pick("तक", "till")} <span className="dh-num">{v}</span></span> : null);

  const core = [
    { label: pick("वार", "Vara"), value: pick(data.vara?.deva, data.vara?.en), ends: null },
    { label: pick("तिथि", "Tithi"), value: `${pick(data.paksha?.deva, data.paksha?.en)} · ${pick(data.tithi?.deva, data.tithi?.en)}`, ends: data.tithi?.ends },
    { label: pick("नक्षत्र", "Nakshatra"), value: pick(data.nakshatra?.deva, data.nakshatra?.en), ends: data.nakshatra?.ends },
  ];

  const suntimes = [
    { icon: SunriseIcon, label: pick("सूर्योदय", "Sunrise"), value: data.sunrise || data.sun_note, tint: "sun" },
    { icon: SunsetIcon, label: pick("सूर्यास्त", "Sunset"), value: data.sunset || data.sun_note, tint: "sun" },
  ];

  const windows = [
    { label: pick("ब्रह्म मुहूर्त", "Brahma Muhurta"), value: m.brahma, warn: false },
    { label: pick("अभिजित मुहूर्त", "Abhijit Muhurta"), value: m.abhijit, warn: false },
    { label: pick("राहु काल", "Rahu Kalam"), value: m.rahu_kalam, warn: true },
    { label: pick("यमगण्ड", "Yamaganda"), value: m.yamaganda, warn: true },
    { label: pick("गुलिक काल", "Gulika Kalam"), value: m.gulika_kalam, warn: true },
  ];

  return (
    <section className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-20" data-testid="home-panchang" data-pf-lang-scope>
      {scopeFontCss && <style dangerouslySetInnerHTML={{ __html: scopeFontCss }} />}
      <div className="rounded-2xl border border-line bg-[var(--surface)] p-5 shadow-[0_1px_2px_rgba(36,28,21,0.04),0_24px_60px_-40px_rgba(36,28,21,0.35)] md:p-8">
        {Header}

        {/* Polar notice — surfaced straight from the payload (midnight sun / polar night) */}
        {data.polar && (
          <div data-testid="home-panchang-polar-notice" className="mt-6 flex items-start gap-3 rounded-lg border-l-2 border-gold bg-[rgba(176,141,87,0.09)] px-4 py-3">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
            <p className="text-sm leading-relaxed text-ink-2">
              {pick(
                "ध्रुवीय क्षेत्र — मध्यरात्रि सूर्य / ध्रुवीय रात्रि के कारण सूर्योदय-आधारित समय (होरा, मुहूर्त) अनुमानित हैं।",
                "Midnight sun / polar night — at this extreme latitude, sunrise-based timings (Hora, Muhurta) are approximate."
              )}
            </p>
          </div>
        )}

        {/* Core limbs */}
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {core.map(({ label, value, ends }) => (
            <div key={label} className="pan-card pan-accent p-5">
              <p className="overline-label text-ink-3 pf-label">{label}</p>
              <p className="mt-2 font-serif-latin text-xl leading-snug text-ink pf-value">{value || "—"}{till(ends)}</p>
            </div>
          ))}
        </div>

        {/* Sunrise / Sunset */}
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {suntimes.map(({ icon: Icon, label, value, tint, span }) => {
            const active = span && timeInSpan(value, now);
            const tintCls = tint === "good"
              ? "border-emerald-500/35 bg-emerald-500/[0.06]"
              : "border-[var(--saffron)]/25 bg-[linear-gradient(135deg,rgba(199,80,0,0.05),rgba(198,138,0,0.045))]";
            return (
              <div key={label} className={`relative rounded-xl border p-5 transition-colors ${tintCls} ${active ? "!border-[var(--saffron)] ring-2 ring-[var(--saffron)]" : ""}`}>
                {active && <span data-testid="home-window-now" className="absolute right-3 top-3 rounded-full bg-[var(--saffron)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">{pick("अभी", "Now")}</span>}
                <p className={`flex items-center gap-1.5 overline-label pf-window-label ${tint === "good" ? "text-emerald-600" : "text-ink-3"}`}><Icon className={`h-4 w-4 ${tint === "good" ? "text-emerald-500" : "text-saffron"}`} /> {label}</p>
                <p className="mt-2 text-xl leading-snug text-ink pf-window-time">{value || "—"}</p>
              </div>
            );
          })}
        </div>

        {/* Auspicious & inauspicious windows — site-themed cards (same tokens as .pan-card) */}
        <div className="mt-6">
          <p className="overline-label mb-4 text-ink-3 pf-window-label">{pick("शुभ एवं अशुभ काल", "Auspicious & Inauspicious Windows")}</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
            {windows.map(({ label, value, warn }) => {
              const active = timeInSpan(value, now);
              const tone = warn ? "bad" : "good";
              return (
                <div key={label} data-testid={`home-window-${label}`} className="pwin" data-on={active ? "true" : "false"} data-win={tone}>
                  <div className="flex items-start justify-between gap-2">
                    <span className="overline-label text-ink-2 pf-window-label">{label}</span>
                    {active && <span data-testid="home-window-now" className="pwin-now shrink-0">{pick("अभी", "Now")}</span>}
                  </div>
                  <span className="pwin-time font-serif-latin text-xl text-ink pf-window-time">{spanParts(value)}</span>
                  {/\d/.test(String(value || "")) && <span className="pwin-q" data-tone={tone}>{warn ? pick("त्याज्य", "Avoid") : pick("शुभ", "Auspicious")}</span>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 border-t border-line pt-5">
          <p className={`text-xs italic leading-relaxed text-ink-3 ${noteFont}`} data-testid="home-panchang-compute-note">
            {uiText("compute_note")} · {uiText("ayanamsa")} ≈ {data.ayanamsa}° · {place}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm">
            <span className="flex items-center gap-1.5 text-ink-3"><Clock className="h-3.5 w-3.5" /> {pick("स्थानीय समय", "Local time")}</span>
            <Link to="/calendar" data-testid="home-panchang-calendar" className="ml-auto inline-flex items-center gap-1.5 text-saffron transition-transform hover:translate-x-0.5">
              {pick("पर्व-पंचांग", "Festival Calendar")} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
